import { Suspense, lazy } from "react";
import { Navigate, useRoutes, useLocation } from "react-router-dom";
// layouts
import MainLayout from "../layouts/main";
import DashboardLayout from "../layouts/dashboard";
import LogoOnlyLayout from "../layouts/LogoOnlyLayout";
// guards
import GuestGuard from "../guards/GuestGuard";
import AuthGuard from "../guards/AuthGuard";
// import RoleBasedGuard from '../guards/RoleBasedGuard';
// config
import { PATH_AFTER_LOGIN } from "../config";
// components
import LoadingScreen from "../components/LoadingScreen";

// ----------------------------------------------------------------------

const Loadable = (Component) => (props) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { pathname } = useLocation();

  return (
    <Suspense fallback={<LoadingScreen isDashboard={pathname.includes("/dashboard")} />}>
      <Component {...props} />
    </Suspense>
  );
};

export default function Router() {
  return useRoutes([
    {
      path: "auth",
      children: [
        {
          path: "login",
          element: (
            <GuestGuard>
              <Login />
            </GuestGuard>
          ),
        },
        {
          path: "register",
          element: (
            <GuestGuard>
              <Register />
            </GuestGuard>
          ),
        },
        { path: "login-unprotected", element: <Login /> },
        { path: "register-unprotected", element: <Register /> },
        { path: "reset-password", element: <ResetPassword /> },
        { path: "new-password", element: <NewPassword /> },
        { path: "verify", element: <VerifyCode /> },
      ],
    },

    // Dashboard Routes
    {
      path: "dashboard",
      element: (
        <AuthGuard>
          <DashboardLayout />
        </AuthGuard>
      ),
      children: [
        { element: <Navigate to={PATH_AFTER_LOGIN} replace />, index: true },
        { path: "app", element: <GeneralApp /> },
        {
          path: "user",
          children: [
            {
              element: <Navigate to="/dashboard/user/profile" replace />,
              index: true,
            },
            { path: ":id", element: <UserProfile /> },
            { path: "cards", element: <UserCards /> },
            { path: "list", element: <List /> },
            { path: "applicant", element: <Applicant /> },
            { path: "applicantbg", element: <ApplicantBG /> },
            { path: "applicantdoc", element: <ApplicantDoc /> },
            { path: "spouse", element: <Spouse /> },
            { path: "spousebg", element: <SpouseBG /> },
            { path: "spousedoc", element: <SpouseDoc /> },
            { path: "fspouse", element: <FSpouse /> },
            { path: "fspousebg", element: <FSpouseBG /> },
            { path: "fspousedoc", element: <FSpouseDoc /> },
            { path: "children", element: <Children /> },
            { path: "documents", element: <Documents /> },
            { path: "account", element: <UserAccount /> },
            { path: "subscription", element: <Pricing /> },
            { path: "assessment", element: <AssessmentForm /> },
          ],
        },
        {
          path: "invoice",
          children: [
            {
              element: <Navigate to="/dashboard/invoice/list" replace />,
              index: true,
            },
            { path: "list", element: <InvoiceList /> },
            { path: ":id", element: <InvoiceDetails /> },
            { path: ":id/edit", element: <InvoiceEdit /> },
            { path: "new", element: <InvoiceCreate /> },
          ],
        },
        {
          path: "profile",
          element: <Profile />,
        },
        {
          path: "blog",
          children: [
            {
              element: <Navigate to="/dashboard/blog/posts" replace />,
              index: true,
            },
            { path: "posts", element: <BlogPosts /> },
            { path: "post/:title", element: <BlogPost /> },
            { path: "new", element: <BlogNewPost /> },
          ],
        },
        {
          path: "service",
          children: [
            {
              element: <Navigate to="/dashboard/service/services" replace />,
              index: true,
            },
            { path: "services", element: <Services /> },
            { path: "service/:title", element: <Service /> },
            { path: "new", element: <NewService /> },
          ],
        },
        {
          path: "contact",
          children: [
            {
              element: <Navigate to="/dashboard/contact/contacts" replace />,
              index: true,
            },
            { path: "contacts", element: <Contacts /> },
            { path: "edit/:id", element: <NewContact /> },
            { path: ":id", element: <Contact /> },
            { path: "new", element: <NewContact /> },
          ],
        },
        {
          path: "mail",
          children: [
            {
              element: <Navigate to="/dashboard/mail/all" replace />,
              index: true,
            },
            { path: "label/:customLabel", element: <Mail /> },
            { path: "label/:customLabel/:mailId", element: <Mail /> },
            { path: ":systemLabel", element: <Mail /> },
            { path: ":systemLabel/:mailId", element: <Mail /> },
          ],
        },
        // {
        //   path: "chat",
        //   children: [
        //     { element: <Navigate to="/dashboard/chat" replace />, index: true },
        //     // { path: "new", element: <Chat /> },
        //     { path: ":conversationKey", element: <Chat /> },
        //   ],
        // },

        { path: "chat", element: <Chat /> },
        { path: "chat/:id", element: <Chat /> },
        // { path: "calendar", element: <Calendar /> },
        // { path: "kanban", element: <Kanban /> },
        // { path: "permission-denied", element: <PermissionDenied /> },
      ],
    },

    // Main Routes
    {
      path: "*",
      element: <LogoOnlyLayout />,
      children: [
        { path: "coming-soon", element: <ComingSoon /> },
        { path: "maintenance", element: <Maintenance /> },
        { path: "pricing", element: <Pricing /> },
        { path: "payment", element: <Payment /> },
        { path: "500", element: <Page500 /> },
        { path: "404", element: <Page404 /> },
        { path: "403", element: <Page403 /> },
        { path: "*", element: <Navigate to="/404" replace /> },
      ],
    },
    {
      path: "/",
      element: <MainLayout />,
      children: [
        { element: <HomePage />, index: true },
        { path: "about-us", element: <About /> },
        { path: "api/mail", element: <Cal /> },
        { path: "start", element: <Start /> },
        { path: "visas/:id", element: <Visas /> },
        { path: "helps/:id", element: <Helps /> },
        { path: "contact-us", element: <ContactUs /> },
        { path: "faqs", element: <Faqs /> },
        { path: "terms", element: <Terms /> },
        { path: "privacy", element: <Privacy /> },
        { path: "careers", element: <Careers /> },
        { path: ":id", element: <UserProfile /> },
        { path: ":userName/posts/:postID", element: <BlogPost /> },
        { path: "applicants/:id", element: <AssessmentView /> },
        { path: "clients/:id", element: <QuestionnerView /> },
        { path: "experts", element: <UserCards /> },
        { path: "assessment", element: <AssessmentForm /> },
      ],
    },
    { path: "*", element: <Navigate to="/404" replace /> },
  ]);
}

// AUTHENTICATION
const Login = Loadable(lazy(() => import("../pages/auth/Login")));
const Register = Loadable(lazy(() => import("../pages/auth/Register")));
const ResetPassword = Loadable(lazy(() => import("../pages/auth/ResetPassword")));
const NewPassword = Loadable(lazy(() => import("../pages/auth/NewPassword")));
const VerifyCode = Loadable(lazy(() => import("../pages/auth/VerifyCode")));

// DASHBOARD

// GENERAL
const GeneralApp = Loadable(lazy(() => import("../pages/dashboard/GeneralApp")));
// INVOICE
const InvoiceList = Loadable(lazy(() => import("../pages/dashboard/InvoiceList")));
const InvoiceDetails = Loadable(lazy(() => import("../pages/dashboard/InvoiceDetails")));
const InvoiceCreate = Loadable(lazy(() => import("../pages/dashboard/InvoiceCreate")));
const InvoiceEdit = Loadable(lazy(() => import("../pages/dashboard/InvoiceEdit")));

// PROFILE
const Profile = Loadable(lazy(() => import("../pages/dashboard/Profile")));

// BLOG
const BlogPosts = Loadable(lazy(() => import("../pages/dashboard/BlogPostsList")));
const BlogPost = Loadable(lazy(() => import("../pages/dashboard/BlogPost")));
const BlogNewPost = Loadable(lazy(() => import("../pages/dashboard/BlogNewPost")));

// SERVICE
const Services = Loadable(lazy(() => import("../pages/dashboard/Services")));
const Service = Loadable(lazy(() => import("../pages/dashboard/Service")));
const NewService = Loadable(lazy(() => import("../pages/dashboard/NewService")));

// CONTACTS
const Contacts = Loadable(lazy(() => import("../pages/dashboard/contacts/Contacts")));
const Contact = Loadable(lazy(() => import("../pages/dashboard/contacts/Contact")));
const NewContact = Loadable(lazy(() => import("../pages/dashboard/contacts/NewContact")));

// USER
const UserProfile = Loadable(lazy(() => import("../pages/dashboard/UserProfile")));
const AssessmentView = Loadable(lazy(() => import("../pages/dashboard/AssessmentView")));
const QuestionnerView = Loadable(lazy(() => import("../pages/dashboard/views/QuestionnerView")));
const UserCards = Loadable(lazy(() => import("../pages/dashboard/UserCards")));
const Applicant = Loadable(lazy(() => import("../pages/dashboard/questioner/Applicant")));
const ApplicantBG = Loadable(lazy(() => import("../pages/dashboard/questioner/ApplicantBackground")));
const ApplicantDoc = Loadable(lazy(() => import("../pages/dashboard/documents/Applicant")));
const Spouse = Loadable(lazy(() => import("../pages/dashboard/questioner/Spouse")));
const SpouseBG = Loadable(lazy(() => import("../pages/dashboard/questioner/SpouseBackground")));
const SpouseDoc = Loadable(lazy(() => import("../pages/dashboard/documents/Spouse")));
const FSpouse = Loadable(lazy(() => import("../pages/dashboard/questioner/FSpouse")));
const FSpouseBG = Loadable(lazy(() => import("../pages/dashboard/questioner/FSpouseBackground")));
const FSpouseDoc = Loadable(lazy(() => import("../pages/dashboard/documents/FSpouse")));
const Children = Loadable(lazy(() => import("../pages/dashboard/questioner/Children")));
const UserAccount = Loadable(lazy(() => import("../pages/dashboard/UserAccount")));
const AssessmentForm = Loadable(lazy(() => import("../sections/@dashboard/user/account/AssessmentForm")));
const List = Loadable(lazy(() => import("../pages/dashboard/UserList")));
const Documents = Loadable(lazy(() => import("../pages/dashboard/documents/Documents")));

// APP
const Chat = Loadable(lazy(() => import("../pages/dashboard/Chat")));
const Mail = Loadable(lazy(() => import("../pages/dashboard/Mail")));
// const Calendar = Loadable(lazy(() => import("../pages/dashboard/Calendar")));
// const Kanban = Loadable(lazy(() => import("../pages/dashboard/Kanban")));

// TEST RENDER PAGE BY ROLE
// const PermissionDenied = Loadable(lazy(() => import("../pages/dashboard/PermissionDenied")));

// MAIN
const HomePage = Loadable(lazy(() => import("../pages/Home")));
const About = Loadable(lazy(() => import("../pages/About")));
const Cal = Loadable(lazy(() => import("../pages/Cal")));
const Start = Loadable(lazy(() => import("../pages/Start")));
const Visas = Loadable(lazy(() => import("../pages/Visas")));
const Helps = Loadable(lazy(() => import("../pages/Helps")));
const ContactUs = Loadable(lazy(() => import("../pages/Contact")));
const Faqs = Loadable(lazy(() => import("../pages/Faqs")));
const Terms = Loadable(lazy(() => import("../pages/Terms")));
const Privacy = Loadable(lazy(() => import("../pages/Privacy")));
const Careers = Loadable(lazy(() => import("../pages/Careers")));
const ComingSoon = Loadable(lazy(() => import("../pages/ComingSoon")));
const Maintenance = Loadable(lazy(() => import("../pages/Maintenance")));
const Pricing = Loadable(lazy(() => import("../pages/Pricing")));
const Payment = Loadable(lazy(() => import("../pages/Payment")));
const Page500 = Loadable(lazy(() => import("../pages/Page500")));
const Page403 = Loadable(lazy(() => import("../pages/Page403")));
const Page404 = Loadable(lazy(() => import("../pages/Page404")));
